import { reactive } from 'sitekit-extensions'
import on, { once } from '../util/elements/on'
import destroys from '../util/elements/destroys'
import anime from 'animejs'
import timelineFactory from '../util/async/timelineFactory'
import {
  coordFromEvent,
  distanceFromPoints,
  directionFromPoints,
} from '../util/math/graphing'
import empty from '../util/object/empty'
import srcset from '../util/elements/get-srcset'

function eachFrame(cb) {
  let cancelInner
  let frame = requestAnimationFrame(() => {
    cancelInner = eachFrame(cb)
    cb()
  })

  return () => {
    cancelInner && cancelInner()
    cancelAnimationFrame(frame)
  }
}

export default function widgetSlider(Site, $) {
  Site.widget('slider', {
    use: [reactive()],
    _create() {
      destroys(this)

      this.btns = this.element.find('.slider-controls .slider-control')
      this.badge = this.element.find('.slide-name')
      this.slideContainer = this.element.find('.slide-container')

      this.slides = []

      // Products are passed directly to the slider
      const products = this.options.products

      this.state = {
        slide: 0,
        activeSlide: 0,
        slides: products.map((obj) => {
          return {
            ...obj,
            name: obj.name.toLowerCase(),
          }
        }),
      }

      const nextOrPrev = on(this.btns, 'click', (e) => {
        const isNext = $(e.currentTarget).is('.slider-next')

        /*
          this.go(bool) accepts a boolean
          if (bool) { go forwards } else { go backwards }
        */
        this.go(isNext)
      })

      const swipe = on(this.element, 'mousedown touchstart', (e) => {
        if (e.type === 'mousedown') e.preventDefault()
        const start = coordFromEvent(e)

        const move = on($(window), 'mousemove touchmove', (e) => {
          if (e.type === 'mousemove') e.preventDefault()
          const current = coordFromEvent(e)
          if (distanceFromPoints(start, current) > 20) {
            const dir = directionFromPoints(current.x, start.x)

            this.go(dir)

            // Cancel events
            move()
            mouseup()
          }
        })

        const mouseup = on($(window), 'mouseup touchend', (e) => {
          if (e.type === 'mouseup') e.preventDefault()
          // Cancel events
          move()
          mouseup()
        })
      })

      this.destroys(nextOrPrev, swipe)
    },
    react() {
      const badgeTm = timelineFactory()

      this.changed('slide', async (prevState) => {
        if (empty(prevState)) return

        const check = this.state.slide
        const length = this.animateOut()

        const slide = this.state.slides[this.getIndex('current')]

        const changeBadge = async () => {
          const waitFor = badgeTm()
          const el = this.badge.get()
          const animate = {
            targets: el,
            duration: 100,
            easing: 'easeOutQuad',
          }

          anime.remove(el)
          await waitFor(
            anime({
              ...animate,
              translateY: 1,
            }).finished
          )
          const width = el[0].getBoundingClientRect().width
          this.badge.text(slide.name)
          this.badge.toggleClass('ellipses', false)

          this.badge.css({ width: 'auto' })

          waitFor(
            anime({
              ...animate,
              duration: 300,
              width: [width, el[0].getBoundingClientRect().width],
            }).finished
          ).then(() => {
            this.badge.toggleClass('ellipses', true)
          })

          await waitFor(
            anime({
              ...animate,
              duration: 200,
              translateY: 0,
            }).finished
          )
        }

        if (this.badge.length) changeBadge()

        await waitFor(length * 0.2)

        if (this.state.slide === check) {
          this.animateIn()
          this.preloadSlides()
        }
      })

      this.changed('slides', (prevState) => {
        if (!this.state.slides.length) return
        this.createSlides()
        this.preloadSlides()
      })
    },
    createSlides() {
      console.log('create slides')
      this.slideContainer.empty()
      const slides = this.state.slides.map(this.createSlide)

      this.slides = slides

      slides.forEach((slide) => {
        const data = slide.data()

        slide.toggleClass('active', this.state.slide === data.index)
        slide.toggleClass('inactive', this.state.slide !== data.index)

        this.slideContainer.append(slide)
      })
    },
    createSlide(slideObj, index = 0) {
      const slide = $(`
      <div 
        class="product-slide slide"
        data-name="${slideObj.name}"
        data-index="${index}"
        data-src="${slideObj.img.url}"
        data-srcset="${srcset(slideObj.img)}"
        id="${slideObj.img.url}"
      >
      <div class="img-container"></div>
      </div>
      `)

      return slide
    },
    animateOut() {
      this.element.find('.active').removeClass('active').addClass('inactive')
      return 300
    },
    animateIn() {
      this.element
        .find(`[data-index="${this.state.slide}"]`)
        .removeClass('inactive')
        .addClass('active')
      return 300
    },
    preloadSlides() {
      const current = this.get('current')
      const next = this.get('next')
      const prev = this.get('prev')

      const check = [current, next, prev]

      const disposers = check.map((el) => {
        if (!el) return
        const data = el.data()
        if (!data.loaded && !data.loading) {
          const img = $('<img/>')

          const off = once(img, 'load', (e) => {
            el.addClass('loaded')
            el.data('loaded', true)
          })

          let stop = eachFrame(() => {
            let w = el.parent().width()
            if (w) {
              console.log('frame called')
              stop()
              img.attr(
                'sizes',
                el.parent().width() * window.devicePixelRatio + 'px'
              )
              el.data('loading', true)
              img.attr('srcset', data.srcset)
              img.attr('src', data.src)
              el.find('.img-container').append(img)
            }
          })

          return () => {
            off()
            stop()
          }
        }
      })

      this.destroys(disposers)
    },

    // Helper Methods
    go(direction) {
      if (typeof direction === 'number') {
        this.next(direction)
      } else if (typeof direction === 'boolean') {
        this[direction ? 'next' : 'prev']()
      }
    },
    find(attr) {
      const el = this.element.find(attr)
      const data = el.data()

      if (data && typeof data.index !== 'undefined') {
        this.goto(data.index)
      }
    },
    get(al) {
      const index = this.getIndex(al)

      return this.slides[index]
    },
    getIndex(numOrAl) {
      const next = (this.state.slide + 1) % this.state.slides.length
      const prev =
        this.state.slide - 1 < 0
          ? this.state.slides.length - 1
          : this.state.slide - 1

      const alias = {
        next,
        prev,
        current: this.state.slide,
      }

      const index =
        typeof numOrAl === 'string'
          ? alias[numOrAl]
          : this.state.slide + numOrAl < 0
          ? this.state.slides.length - 1
          : this.state.slide + numOrAl

      return index
    },
    goto(slide) {
      this.setState({
        slide: Math.abs(slide % this.state.slides.length),
      })
    },
    next(amount = 1) {
      const next =
        this.state.slide + amount < 0
          ? this.state.slides.length - 1
          : this.state.slide + amount
      this.setState({
        slide: next % this.state.slides.length,
      })
    },
    prev(amount = 1) {
      const next =
        this.state.slide - amount < 0
          ? this.state.slides.length - 1
          : this.state.slide - amount
      this.setState({
        slide: next % this.state.slides.length,
      })
    },
  })
}

function waitFor(time) {
  return new Promise((resolve) => setTimeout(resolve, time))
}

function encode(obj) {
  return encodeURIComponent(JSON.stringify(obj))
}

function decode(str) {
  return JSON.parse(decodeURIComponent(str))
}
function reverse(arr) {
  return [...arr].reverse()
}
